import { useConfig } from '@/context/ConfigContext'
import {
  exchangeSupportSessionCode,
  parseSupportSessionFragment,
} from '@/lib/support-session'
import { EyeIcon } from '@heroicons/react/24/outline'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, Spinner } from '@droidmap/frontend-ui'
import { HttpResponseError } from '@droidmap/shared-utils'

/**
 * Where the admin console opens a support session (droidmap#2651). Top-level,
 * like `/share/$token`: it must load whether or not the browser holds a
 * customer session, and the one-time code in the fragment is the credential.
 *
 * The code is exchanged once, the session is kept in this tab's
 * sessionStorage, and the page reloads onto the path the staff member chose.
 * Reloading is what switches the app onto the support-session auth provider.
 */
export const Route = createFileRoute('/support-session')({
  component: SupportSessionPage,
})

/**
 * `invalid`: the admin service refused the code (used, expired, its session
 * ended, or malformed), so only a new session helps. `unreachable`: anything
 * else, a network failure or a 5xx, which leaves the code good, since only a
 * successful exchange spends it.
 */
type ExchangeState = 'exchanging' | 'invalid' | 'unreachable'

const failureOf = (error: unknown): ExchangeState =>
  error instanceof HttpResponseError &&
  error.status !== undefined &&
  error.status >= 400 &&
  error.status < 500
    ? 'invalid'
    : 'unreachable'

export function SupportSessionPage() {
  const { ENV } = useConfig()
  // Read at mount: the effect below takes the fragment out of the address bar.
  const [fragment] = useState(() =>
    parseSupportSessionFragment(window.location.hash),
  )
  const [state, setState] = useState<ExchangeState>(
    fragment.code ? 'exchanging' : 'invalid',
  )
  // A strict-mode double effect must not spend the single-use code twice.
  const started = useRef(false)

  const exchange = useCallback(() => {
    const { code, to } = fragment
    if (!code) {
      return
    }
    setState('exchanging')
    exchangeSupportSessionCode(code, ENV)
      .then(() => window.location.replace(to))
      .catch((error: unknown) => setState(failureOf(error)))
  }, [ENV, fragment])

  useEffect(() => {
    if (started.current) {
      return
    }
    started.current = true
    // Out of the address bar and history before anything else happens.
    window.history.replaceState(null, '', window.location.pathname)
    exchange()
  }, [exchange])

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-md text-center space-y-3">
        <EyeIcon className="h-10 w-10 mx-auto text-warning" aria-hidden />
        {state === 'invalid' ? (
          <>
            <h1 className="text-xl font-semibold">
              Could not start the support session
            </h1>
            <p className="text-sm text-text-dim">
              The link has already been used or has expired. Start a new session
              from the admin console.
            </p>
          </>
        ) : state === 'unreachable' ? (
          <>
            <h1 className="text-xl font-semibold">
              Could not reach DroidMap to start the session
            </h1>
            <p className="text-sm text-text-dim">
              The link is still good for a minute after it was made.
            </p>
            <Button size="sm" onClick={exchange}>
              Try again
            </Button>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Starting support session…</h1>
            <Spinner />
          </>
        )}
      </div>
    </div>
  )
}
